import {
  type ChangeEvent,
  type SubmitEvent,
  Fragment,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { DataTable } from "../../components/DataTable/index.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import {
  mobileActionButtonClassNames,
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../../mobile/mobilePrimitives.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { helpers } from "../../util/helpers.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import { logEvent } from "../../util/logEvent.ts";
import { toWorker } from "../../util/toWorker.ts";
import { getCols } from "../../util/getCols.ts";
import { useLocalPartial } from "../../util/local.ts";
import type { View } from "../../../../../common/types.ts";
import { PHASE } from "../../../../../common/constants.ts";
import { i18n } from "../../util/useT.ts";
import { wrappedPlayerNameLabels } from "../../components/PlayerNameLabels.tsx";
import type { DataTableRow } from "../../components/DataTable/index.tsx";
import type { FooterRow } from "../../components/DataTable/Footer.tsx";
import { wrappedPosition } from "../../util/positionNames.tsx";
import {
  MAX_LEVEL,
  coachingEffect,
  facilitiesEffectAttendance,
  facilitiesEffectMood,
  healthEffect,
  levelToAmount,
  levelToEffect,
  scoutingEffectCutoff,
  scoutingEffectStddev,
} from "../../../../../common/budgetLevels.ts";
import { CurrencyInputGroup } from "../../components/CurrencyInputGroup.tsx";
import { useBlocker } from "../../hooks/useBlocker.ts";
import { gradientStyleFactory } from "../../util/gradientStyleFactory.ts";
import { BarGraph } from "./BarGraph.tsx";
import { getAdjustedTicketPrice } from "../../../../../common/getAdjustedTicketPrice.ts";
import {
  getTeamFinancePrimaryActionPlan,
  getTeamFinanceRisk,
  resolveTeamFinancesRosterContext,
  type TeamFinanceRisk,
} from "../teamManagementDepth.ts";
import {
  type BudgetInvestmentState,
  type FinanceCapState,
  type TeamHypeState,
  getBudgetInvestmentStatus,
  getFinanceCapStatus,
  getTeamHypeStatus,
} from "./financeStatusMetrics.ts";

const paddingLeft85 = { paddingLeft: 85 };

const formatTicketPrice = (ticketPrice: number) => {
  // Never show just one decimal place, because it's cents
  if (!Number.isInteger(ticketPrice) && Number.isInteger(ticketPrice * 10)) {
    return ticketPrice.toFixed(2);
  }
  return String(ticketPrice);
};

type BudgetKey = "scouting" | "coaching" | "health" | "facilities";

const roundEffect = (effect: number, includePlusSign: boolean) => {
  return `${includePlusSign && effect >= 0 ? "+" : ""}${effect.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}`;
};

const GradientStyle = ({ children, level }: { children: ReactNode; level: number }) => {
  const gradientStyle = gradientStyleFactory(-0.9, -0.05, 0.05, 0.9);

  return <span style={gradientStyle(levelToEffect(level))}>{children}</span>;
};

const BudgetEffect = ({ type, level }: { type: BudgetKey; level: number }) => {
  if (type === "scouting") {
    const effectCutoff = scoutingEffectCutoff(level);
    const effectStddev = scoutingEffectStddev(level);
    return (
      <>
        {i18n.t("teamFinances.ratingsErrorStddev", {
          ns: "ui",
          defaultValue: "Ratings error stddev: ",
        })}
        <GradientStyle level={level}>{roundEffect(effectStddev, false)}</GradientStyle>
        <br />
        {i18n.t("teamFinances.maxRatingsError", {
          ns: "ui",
          defaultValue: "Maximum ratings error: ",
        })}
        <GradientStyle level={level}>{effectCutoff}</GradientStyle>
      </>
    );
  }

  if (type === "coaching") {
    const effect = coachingEffect(level);
    if (effect === 0) {
      return i18n.t("teamFinances.normalProgs", { ns: "ui", defaultValue: "Normal progs" });
    }

    return (
      <>
        <GradientStyle level={level}>{roundEffect(100 * effect, true)}%</GradientStyle>
        {i18n.t("teamFinances.positiveProgs", { ns: "ui", defaultValue: " positive progs" })}
        <br />
        <GradientStyle level={level}>{roundEffect(-100 * effect, true)}%</GradientStyle>
        {i18n.t("teamFinances.negativeProgs", { ns: "ui", defaultValue: " negative progs" })}
      </>
    );
  }

  if (type === "health") {
    const effect = healthEffect(level);
    if (effect === 0) {
      return i18n.t("teamFinances.normalInjuryDuration", {
        ns: "ui",
        defaultValue: "Normal injury duration",
      });
    }

    return (
      <>
        <GradientStyle level={level}>{roundEffect(100 * effect, true)}%</GradientStyle>
        {i18n.t("teamFinances.injuryDuration", { ns: "ui", defaultValue: " injury duration" })}
      </>
    );
  }

  const effectMood = facilitiesEffectMood(level);
  const effectAttendance = facilitiesEffectAttendance(level);
  return (
    <>
      <GradientStyle level={level}>{roundEffect(effectMood, true)}</GradientStyle>
      {i18n.t("teamFinances.playerMood", { ns: "ui", defaultValue: " player mood" })}
      <br />
      <GradientStyle level={level}>{roundEffect(100 * effectAttendance, true)}%</GradientStyle>
      {i18n.t("teamFinances.ticketDemand", { ns: "ui", defaultValue: " ticket demand" })}
    </>
  );
};

const budgetInvestmentLabel = (state?: BudgetInvestmentState) => {
  if (state === "low") {
    return i18n.t("teamFinances.investmentStatus.low", {
      ns: "ui",
      defaultValue: "Low investment",
    });
  }
  if (state === "strong") {
    return i18n.t("teamFinances.investmentStatus.strong", {
      ns: "ui",
      defaultValue: "Strong investment",
    });
  }
  if (state === "balanced") {
    return i18n.t("teamFinances.investmentStatus.balanced", {
      ns: "ui",
      defaultValue: "Balanced investment",
    });
  }
  return i18n.t("teamFinances.valueUnavailable", {
    ns: "ui",
    defaultValue: "Not available",
  });
};

export const teamHypeLabel = (state?: TeamHypeState) => {
  if (state === "low") {
    return i18n.t("teamFinances.hypeStatus.low", {
      ns: "ui",
      defaultValue: "Low enthusiasm",
    });
  }
  if (state === "high") {
    return i18n.t("teamFinances.hypeStatus.high", {
      ns: "ui",
      defaultValue: "High enthusiasm",
    });
  }
  if (state === "steady") {
    return i18n.t("teamFinances.hypeStatus.steady", {
      ns: "ui",
      defaultValue: "Steady interest",
    });
  }
  return i18n.t("teamFinances.valueUnavailable", {
    ns: "ui",
    defaultValue: "Not available",
  });
};

const capStatusLabel = (state: FinanceCapState | undefined, capSpace: number) => {
  const amount = helpers.formatCurrency(Math.abs(capSpace), "M");
  if (state === "over") {
    return i18n.t("leagueDashboard.capOverStatus", {
      ns: "ui",
      defaultValue: "{{amount}} over the limit",
      amount,
    });
  }
  if (state === "near") {
    return i18n.t("leagueDashboard.capNearStatus", {
      ns: "ui",
      defaultValue: "Near the limit",
    });
  }
  if (state === "available") {
    return i18n.t("leagueDashboard.capAvailableStatus", {
      ns: "ui",
      defaultValue: "{{amount}} available",
      amount,
    });
  }
  return i18n.t("teamFinances.valueUnavailable", {
    ns: "ui",
    defaultValue: "Not available",
  });
};

export const FinancesForm = ({
  autoTicketPrice,
  challengeNoRatings,
  gameSimInProgress,
  otherTeamTicketPrices,
  phase,
  salaryCap,
  spectator,
  t,
  tid,
  userTid,
  hideHeading = false,
}: Pick<
  View<"teamFinances">,
  | "autoTicketPrice"
  | "challengeNoRatings"
  | "otherTeamTicketPrices"
  | "phase"
  | "salaryCap"
  | "spectator"
  | "t"
  | "tid"
  | "userTid"
> & {
  gameSimInProgress: boolean;
  hideHeading?: boolean;
}) => {
  const { dirty, setDirty } = useBlocker({
    variant: "team-finances-unsaved",
  });

  const [state, setState] = useState({
    saving: false,
    coaching: String(t.budget.coaching),
    facilities: String(t.budget.facilities),
    health: String(t.budget.health),
    scouting: String(t.budget.scouting),
    ticketPrice: formatTicketPrice(t.budget.ticketPrice),
    adjustForInflation: t.adjustForInflation,
    autoTicketPrice: t.autoTicketPrice,
  });

  useEffect(() => {
    if (!dirty) {
      setState((state2) => ({
        ...state2,
        coaching: String(t.budget.coaching),
        facilities: String(t.budget.facilities),
        health: String(t.budget.health),
        scouting: String(t.budget.scouting),
        ticketPrice: formatTicketPrice(t.budget.ticketPrice),
        adjustForInflation: t.adjustForInflation,
        autoTicketPrice: t.autoTicketPrice,
      }));
    }
  }, [dirty, t]);

  const setStateValue = <Name extends Exclude<keyof typeof state, "saving">>(
    name: Name,
    value: (typeof state)[Name],
  ) => {
    setState((state2) => ({
      ...state2,
      [name]: value,
    }));
    setDirty(true);
  };

  const handleChange =
    (name: Exclude<keyof typeof state, "dirty" | "saving">) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (name === "adjustForInflation" || name === "autoTicketPrice") {
        setStateValue(name, event.target.checked);
      } else {
        setStateValue(name, event.target.value);
      }
    };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    setState((state2) => ({ ...state2, saving: true }));

    const budgetAmounts = {
      // Convert from [millions of dollars] to [thousands of dollars] rounded to the nearest $10k
      coaching: helpers.bound(Math.round(helpers.localeParseFloat(state.coaching)), 1, MAX_LEVEL),
      facilities: helpers.bound(
        Math.round(helpers.localeParseFloat(state.facilities)),
        1,
        MAX_LEVEL,
      ),
      health: helpers.bound(Math.round(helpers.localeParseFloat(state.health)), 1, MAX_LEVEL),
      scouting: helpers.bound(Math.round(helpers.localeParseFloat(state.scouting)), 1, MAX_LEVEL),

      // Already in [dollars]
      ticketPrice: helpers.bound(
        helpers.localeParseFloat(helpers.localeParseFloat(state.ticketPrice).toFixed(2)),
        0,
        Infinity,
      ),
    };

    await toWorker("main", "updateBudget", {
      budgetLevels: budgetAmounts,
      adjustForInflation: state.adjustForInflation || state.autoTicketPrice,
      autoTicketPrice: state.autoTicketPrice,
    });

    logEvent({
      type: "success",
      text: i18n.t("teamFinances.saved", {
        ns: "ui",
        defaultValue: "Saved team finance settings.",
      }),
      saveToDb: false,
    });

    setState((state2) => ({
      ...state2,
      saving: false,
    }));
    setDirty(false);
  };

  const warningMessage =
    gameSimInProgress && tid === userTid && !spectator ? (
      <div className="text-danger mb-2">
        {i18n.t("teamFinances.stopSimulation", {
          ns: "ui",
          defaultValue: "Stop game simulation to edit.",
        })}
      </div>
    ) : null;

  const formDisabled = gameSimInProgress || tid !== userTid || spectator;

  const expenseCategories: {
    key: BudgetKey;
    title: string;
  }[] = [
    {
      key: "scouting",
      title: i18n.t("teamFinances.scouting", { ns: "ui", defaultValue: "Scouting" }),
    },
    {
      key: "coaching",
      title: i18n.t("teamFinances.coaching", { ns: "ui", defaultValue: "Coaching" }),
    },
    {
      key: "health",
      title: i18n.t("teamFinances.health", { ns: "ui", defaultValue: "Health" }),
    },
    {
      key: "facilities",
      title: i18n.t("teamFinances.facilities", { ns: "ui", defaultValue: "Facilities" }),
    },
  ];

  const ticketPriceForRank = state.autoTicketPrice
    ? autoTicketPrice
    : helpers.localeParseFloat(state.ticketPrice);
  const ticketPriceRankIndex = otherTeamTicketPrices.findIndex(
    (price) => price < ticketPriceForRank,
  );
  const ticketPriceRank =
    ticketPriceRankIndex < 0 ? otherTeamTicketPrices.length + 1 : ticketPriceRankIndex + 1;

  const [projectedAttendance, setProjectedAttendance] = useState<number | undefined>();
  useEffect(() => {
    let mounted = true;
    (async () => {
      const attendance = await toWorker("main", "getProjectedAttendance", {
        tid,
        ticketPrice: ticketPriceForRank,
      });

      if (mounted) {
        setProjectedAttendance(attendance);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [ticketPriceForRank, tid]);

  return (
    <form onSubmit={handleSubmit} className="mb-3 team-finances-form">
      {warningMessage}
      {!hideHeading ? (
        <h2>
          {i18n.t("teamFinances.expenseLevels", {
            ns: "ui",
            defaultValue: "Expense levels",
          })}
        </h2>
      ) : null}
      <div className="d-flex flex-column gap-3">
        {expenseCategories.map((expenseCategory) => {
          const level = state[expenseCategory.key];
          const levelInt = helpers.bound(
            Math.round(helpers.localeParseFloat(state[expenseCategory.key])),
            1,
            MAX_LEVEL,
          );
          const levelThree = t.expenseLevelsLastThree[expenseCategory.key];
          const currentInvestmentStatus = getBudgetInvestmentStatus(levelThree);
          const proposedInvestmentStatus = getBudgetInvestmentStatus(levelInt);
          const proposedInvestmentLabel = budgetInvestmentLabel(proposedInvestmentStatus.state);
          const sliderValue = Number.isNaN(levelInt) ? 1 : levelInt;

          const formDisabledKey =
            formDisabled || (expenseCategory.key === "scouting" && challengeNoRatings);
          const sliderId = `team-finances-${expenseCategory.key}-level`;

          return (
            <div data-team-finances-budget-key={expenseCategory.key} key={expenseCategory.key}>
              <h3>
                {i18n.t("teamFinances.expenseLevelTitle", {
                  ns: "ui",
                  defaultValue: "{{title}} expense level",
                  title: expenseCategory.title,
                })}
              </h3>
              <div className="d-flex team-finances-expense-control">
                <div>
                  <div
                    className="input-group mb-1 team-finances-expense-stepper"
                    style={{
                      width: 120,
                    }}
                  >
                    <button
                      className="btn btn-secondary"
                      data-team-finances-budget-decrement={expenseCategory.key}
                      type="button"
                      disabled={formDisabledKey || Number.isNaN(levelInt) || levelInt <= 1}
                      onClick={() => {
                        setStateValue(expenseCategory.key, String(levelInt - 1));
                      }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      className="form-control text-center"
                      disabled={formDisabledKey}
                      onChange={handleChange(expenseCategory.key)}
                      value={level}
                      inputMode="numeric"
                    />
                    <button
                      className="btn btn-secondary"
                      data-team-finances-budget-increment={expenseCategory.key}
                      type="button"
                      disabled={formDisabledKey || Number.isNaN(levelInt) || levelInt >= MAX_LEVEL}
                      onClick={() => {
                        setStateValue(expenseCategory.key, String(levelInt + 1));
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div className="team-finances-expense-slider mb-2">
                    <div className="team-finances-expense-slider-readout">
                      <span
                        className={`team-finances-expense-slider-status team-finances-expense-slider-status-${proposedInvestmentStatus.state ?? "unavailable"}`}
                      >
                        {proposedInvestmentLabel}
                      </span>
                      <output htmlFor={sliderId}>{sliderValue}</output>
                    </div>
                    <input
                      aria-label={i18n.t("teamFinances.expenseLevelTitle", {
                        ns: "ui",
                        defaultValue: "{{title}} expense level",
                        title: expenseCategory.title,
                      })}
                      aria-valuetext={`${proposedInvestmentLabel}: ${sliderValue}`}
                      className="form-range team-finances-expense-range"
                      data-team-finances-budget-slider={expenseCategory.key}
                      disabled={formDisabledKey}
                      id={sliderId}
                      max={MAX_LEVEL}
                      min={1}
                      onChange={handleChange(expenseCategory.key)}
                      step={1}
                      type="range"
                      value={sliderValue}
                    />
                    <div aria-hidden="true" className="team-finances-expense-slider-scale">
                      <span>{budgetInvestmentLabel("low")}</span>
                      <span>{budgetInvestmentLabel("balanced")}</span>
                      <span>{budgetInvestmentLabel("strong")}</span>
                    </div>
                  </div>
                  <div>
                    {i18n.t("teamFinances.avgLast3", {
                      ns: "ui",
                      defaultValue: "Average of last 3 seasons: {{value}}",
                      value: levelThree,
                    })}
                  </div>
                  <div>
                    {i18n.t("teamFinances.currentCost", {
                      ns: "ui",
                      defaultValue: "Current annual cost: ",
                    })}
                    {Number.isNaN(levelInt)
                      ? i18n.t("teamFinances.valueUnavailable", {
                          ns: "ui",
                          defaultValue: "Not available",
                        })
                      : helpers.formatCurrency(
                          levelToAmount(levelInt, salaryCap * 1000) / 1000,
                          "M",
                        )}
                  </div>
                </div>
                <div className="row ms-3 team-finances-expense-effects" style={{ width: 350 }}>
                  <div className="col-12 col-lg-6">
                    <MobileStatusMetric
                      detail={<BudgetEffect type={expenseCategory.key} level={levelThree} />}
                      label={i18n.t("teamFinances.currentEffect", {
                        ns: "ui",
                        defaultValue: "Current effect",
                      })}
                      score={currentInvestmentStatus.score}
                      value={budgetInvestmentLabel(currentInvestmentStatus.state)}
                    />
                  </div>
                  {!Number.isNaN(levelInt) && levelInt !== levelThree ? (
                    <div className="col-12 col-lg-6">
                      <MobileStatusMetric
                        detail={<BudgetEffect type={expenseCategory.key} level={levelInt} />}
                        label={i18n.t("teamFinances.after3Years", {
                          ns: "ui",
                          defaultValue: "After 3 years at level {{level}}",
                          level: levelInt,
                        })}
                        score={proposedInvestmentStatus.score}
                        value={budgetInvestmentLabel(proposedInvestmentStatus.state)}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <h2 className="mt-4">
        {i18n.t("teamFinances.ticketPrice", { ns: "ui", defaultValue: "Ticket price" })}
      </h2>
      <div className="d-flex align-items-center team-finances-ticket-row">
        <CurrencyInputGroup
          style={{
            width: 155,
          }}
        >
          {state.autoTicketPrice ? (
            <input
              type="text"
              className="form-control"
              disabled
              value={formatTicketPrice(autoTicketPrice)}
            />
          ) : (
            <input
              type="text"
              className="form-control"
              disabled={formDisabled || state.autoTicketPrice}
              onChange={handleChange("ticketPrice")}
              value={state.ticketPrice}
              inputMode="decimal"
            />
          )}
        </CurrencyInputGroup>
        <div className="ms-3">
          <div>
            {i18n.t("teamFinances.ticketPriceRank", {
              ns: "ui",
              defaultValue: "Ticket price rank: #{{rank}}",
              rank: ticketPriceRank,
            })}
          </div>
          <div>
            {i18n.t("teamFinances.projectedAttendance", {
              ns: "ui",
              defaultValue: "Projected attendance: ",
            })}
            {projectedAttendance !== undefined ? helpers.numberWithCommas(projectedAttendance) : ""}
          </div>
        </div>
      </div>
      {phase === PHASE.PLAYOFFS ? (
        <div className="mt-1 text-warning">
          {i18n.t("teamFinances.playoffsPrice", { ns: "ui", defaultValue: "Playoffs price: " })}
          {helpers.formatCurrency(
            getAdjustedTicketPrice(
              state.autoTicketPrice ? autoTicketPrice : helpers.localeParseFloat(state.ticketPrice),
              true,
            ),
          )}
        </div>
      ) : null}
      <div className="mt-1 d-flex team-finances-options-row">
        <div className="form-check">
          <label className="form-check-label">
            <input
              className="form-check-input"
              onChange={handleChange("autoTicketPrice")}
              type="checkbox"
              checked={state.autoTicketPrice}
              disabled={formDisabled}
            />
            {i18n.t("teamFinances.autoTicketPrice", {
              ns: "ui",
              defaultValue: "Auto ticket price",
            })}
          </label>
        </div>
        <div className="form-check ms-4 team-finances-inflation-check">
          <label className="form-check-label">
            <input
              className="form-check-input"
              onChange={handleChange("adjustForInflation")}
              type="checkbox"
              checked={state.adjustForInflation || state.autoTicketPrice}
              disabled={formDisabled || state.autoTicketPrice}
            />
            {i18n.t("teamFinances.autoAdjustInflation", {
              ns: "ui",
              defaultValue: "Auto adjust for inflation",
            })}
          </label>
        </div>
      </div>
      {tid === userTid && !spectator ? (
        <div className="mt-4 team-finances-save-row" style={paddingLeft85}>
          <button
            className="btn btn-large btn-primary"
            disabled={formDisabled || state.saving}
            type="submit"
          >
            {i18n
              .t("teamFinances.saveButton", {
                ns: "ui",
                defaultValue: "Save expense levels\nand ticket price",
              })
              .split("\n")
              .map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </span>
              ))}
          </button>
        </div>
      ) : null}
    </form>
  );
};

export const PayrollInfo = ({
  luxuryPayroll,
  luxuryTax,
  luxuryTaxAmount,
  minPayroll,
  minPayrollAmount,
  payroll,
  salaryCap,
  salaryCapType,
}: Pick<
  View<"teamFinances">,
  | "luxuryPayroll"
  | "luxuryTax"
  | "luxuryTaxAmount"
  | "minPayroll"
  | "minPayrollAmount"
  | "payroll"
  | "salaryCap"
  | "salaryCapType"
>) => {
  const statusDirection = (above: boolean) =>
    above
      ? i18n.t("teamFinances.above", { ns: "ui", defaultValue: "above" })
      : i18n.t("teamFinances.below", { ns: "ui", defaultValue: "below" });
  const projectedPenalty = (amount: number) =>
    i18n.t("teamFinances.projectedPenalty", {
      ns: "ui",
      defaultValue: "projected penalty: {{amount}}",
      amount: helpers.formatCurrency(amount, "M"),
    });

  const parts = [
    <>
      {statusDirection(payroll > minPayroll)}{" "}
      {i18n.t("teamFinances.minimumPayrollLimit", {
        ns: "ui",
        defaultValue: "the minimum payroll limit",
      })}{" "}
      (<b>{helpers.formatCurrency(minPayroll, "M")}</b>
      {minPayrollAmount > 0 ? (
        <>
          , <span className="text-danger">{projectedPenalty(minPayrollAmount)}</span>
        </>
      ) : null}
      )
    </>,
  ];

  if (salaryCapType !== "none") {
    parts.push(
      <>
        {statusDirection(payroll > salaryCap)}{" "}
        {i18n.t("teamFinances.salaryCap", {
          ns: "ui",
          defaultValue: "the salary cap",
        })}{" "}
        (<b>{helpers.formatCurrency(salaryCap, "M")}</b>)
      </>,
    );
  }

  if (luxuryTax !== 0) {
    parts.push(
      <>
        {statusDirection(payroll > luxuryPayroll)}{" "}
        {i18n.t("teamFinances.luxuryTaxLimit", {
          ns: "ui",
          defaultValue: "the luxury tax limit",
        })}{" "}
        (<b>{helpers.formatCurrency(luxuryPayroll, "M")}</b>
        {luxuryTaxAmount > 0 ? (
          <>
            , <span className="text-danger">{projectedPenalty(luxuryTaxAmount)}</span>
          </>
        ) : null}
        )
      </>,
    );
  }

  return (
    <>
      <p className="d-none d-md-block">
        {i18n.t("teamFinances.currentPayrollPrefix", {
          ns: "ui",
          defaultValue: "The current payroll",
        })}{" "}
        (<b>{helpers.formatCurrency(payroll, "M")}</b>){" "}
        {i18n.t("teamFinances.is", { ns: "ui", defaultValue: "is" })}{" "}
        {parts.length === 1 ? (
          parts[0]
        ) : parts.length <= 2 ? (
          <>
            {parts[0]} {i18n.t("teamFinances.and", { ns: "ui", defaultValue: "and" })} {parts[1]}
          </>
        ) : (
          <>
            {parts.slice(0, -1).map((part, i) => (
              <Fragment key={i}>
                {part}
                {i18n.t("teamFinances.listSeparator", {
                  ns: "ui",
                  defaultValue: ", ",
                })}
              </Fragment>
            ))}
            {i18n.t("teamFinances.and", { ns: "ui", defaultValue: "and" })} {parts.at(-1)}
          </>
        )}
        .
      </p>
    </>
  );
};

const stripTrailingColon = (text: string) => text.replace(/:\s*$/, "");

// The stat-row label already names the target (tax line / limit), so the value
// is a single natural two-state phrase — "已超出 X" / "还差 X" / "刚好持平" — with
// the amount embedded, instead of the stilted `${amount} ${direction} ${target}`
// concatenation that repeated the tax-line word and broke Chinese word order.
const formatFinanceDistance = (distance: number) => {
  const direction = distance > 0 ? "below" : distance < 0 ? "over" : "at";
  const amount = helpers.formatCurrency(Math.abs(distance), "M");
  const value =
    direction === "over"
      ? i18n.t("teamFinances.distanceValueOver", {
          ns: "ui",
          defaultValue: "{{amount}} over",
          amount,
        })
      : direction === "below"
        ? i18n.t("teamFinances.distanceValueUnder", {
            ns: "ui",
            defaultValue: "{{amount}} under",
            amount,
          })
        : i18n.t("teamFinances.distanceValueAt", {
            ns: "ui",
            defaultValue: "At the line",
          });

  return {
    amount,
    direction,
    value,
  };
};

const financeRiskStatus = (financeRisk: TeamFinanceRisk) => {
  if (financeRisk.severity === "danger") {
    return {
      score: 18,
      value: i18n.t("leagueDashboard.seasonGoalRisk.danger", {
        ns: "ui",
        defaultValue: "At risk",
      }),
    };
  }
  if (financeRisk.severity === "warning") {
    return {
      score: 55,
      value: i18n.t("leagueDashboard.seasonGoalRisk.watch", {
        ns: "ui",
        defaultValue: "Watch",
      }),
    };
  }
  return {
    score: 88,
    value: i18n.t("leagueDashboard.seasonGoalRisk.clear", {
      ns: "ui",
      defaultValue: "On track",
    }),
  };
};

type TeamFinancesBarDataRow = View<"teamFinances">["barData"][number];

// v5Content: the cap-health numbers ARE the first-screen content. This renders
// the stat-row metrics + projected-penalty warnings + the multi-year commitment
// timeline directly into the content hero — no decision-summary narration card
// and no "signing/trade/extension limit" explainer cards (法则一).
export const TeamFinancesMobileSummary = ({
  contractTotals,
  financeRisk,
  luxuryPayroll,
  luxuryTax,
  luxuryTaxAmount,
  minPayroll,
  minPayrollAmount,
  payroll,
  salariesSeasons,
  salaryCap,
  salaryCapType,
}: Pick<
  View<"teamFinances">,
  | "contractTotals"
  | "luxuryPayroll"
  | "luxuryTax"
  | "luxuryTaxAmount"
  | "minPayroll"
  | "minPayrollAmount"
  | "payroll"
  | "salariesSeasons"
  | "salaryCap"
  | "salaryCapType"
> & {
  financeRisk: TeamFinanceRisk;
}) => {
  const [allYearsOpen, setAllYearsOpen] = useState(false);
  const payrollLimit =
    salaryCapType !== "none" ? salaryCap : luxuryTax !== 0 ? luxuryPayroll : minPayroll;
  const taxOrLimitDistance = (luxuryTax !== 0 ? luxuryPayroll : payrollLimit) - payroll;
  const financeDistance = formatFinanceDistance(taxOrLimitDistance);
  const nextSeasonIndex = salariesSeasons.length > 1 ? 1 : 0;
  const nextSeasonPayroll = contractTotals[nextSeasonIndex] ?? payroll;
  const currentCapSpace = payrollLimit - payroll;
  const nextSeasonCapSpace = payrollLimit - nextSeasonPayroll;
  const currentCapStatus = getFinanceCapStatus({
    capSpace: currentCapSpace,
    payrollLimit,
  });
  const nextSeasonCapStatus = getFinanceCapStatus({
    capSpace: nextSeasonCapSpace,
    payrollLimit,
  });
  const payrollStatus = financeRiskStatus(financeRisk);
  const timelineItems = salariesSeasons.slice(0, 5).map((season, index) => {
    const committed = contractTotals[index] ?? 0;
    const distance = payrollLimit - committed;

    return {
      capStatus: getFinanceCapStatus({ capSpace: distance, payrollLimit }),
      committed,
      distance,
      season,
    };
  });
  const highestPressureYear = timelineItems.reduce<(typeof timelineItems)[number] | undefined>(
    (current, item) => {
      if (!current || item.distance < current.distance) {
        return item;
      }
      return current;
    },
    undefined,
  );
  const timelinePreviewLimit = 2;
  const visibleTimelineItems = allYearsOpen
    ? timelineItems
    : timelineItems.slice(0, timelinePreviewLimit);

  const warnings = [
    minPayrollAmount > 0
      ? i18n.t("teamFinances.projectedPenalty", {
          ns: "ui",
          defaultValue: "projected penalty: {{amount}}",
          amount: helpers.formatCurrency(minPayrollAmount, "M"),
        })
      : undefined,
    luxuryTaxAmount > 0
      ? i18n.t("teamFinances.projectedPenalty", {
          ns: "ui",
          defaultValue: "projected penalty: {{amount}}",
          amount: helpers.formatCurrency(luxuryTaxAmount, "M"),
        })
      : undefined,
  ].filter((warning): warning is string => warning !== undefined);

  return (
    <>
      <div className="team-finances-mobile-summary-grid">
        <MobileStatusMetric
          detail={helpers.formatCurrency(currentCapSpace, "M")}
          label={i18n.t("teamFinances.mobileCapSpaceLabel", {
            ns: "ui",
            defaultValue: "Cap space",
          })}
          score={currentCapStatus.score}
          value={capStatusLabel(currentCapStatus.state, currentCapSpace)}
        />
        <MobileStatusMetric
          detail={helpers.formatCurrency(nextSeasonCapSpace, "M")}
          label={i18n.t("teamFinances.nextSeasonPressure", {
            ns: "ui",
            defaultValue: "Next-yr space",
          })}
          score={nextSeasonCapStatus.score}
          value={capStatusLabel(nextSeasonCapStatus.state, nextSeasonCapSpace)}
        />
        <MobileStatRow
          label={
            luxuryTax !== 0
              ? i18n.t("teamFinances.taxLineDistance", {
                  ns: "ui",
                  defaultValue: "Tax line",
                })
              : i18n.t("teamFinances.limitDistance", {
                  ns: "ui",
                  defaultValue: "Limit",
                })
          }
          tone={financeDistance.direction === "over" ? "warning" : "neutral"}
          value={
            <span data-team-finance-distance-direction={financeDistance.direction}>
              {financeDistance.value}
            </span>
          }
        />
        <MobileStatusMetric
          detail={helpers.formatCurrency(payroll, "M")}
          label={i18n.t("leagueDashboard.riskSalary", {
            ns: "ui",
            defaultValue: "Salary pressure",
          })}
          score={payrollStatus.score}
          value={payrollStatus.value}
        />
      </div>
      {warnings.length > 0 ? (
        <div className="team-finances-mobile-summary-warning text-danger mt-2">
          {warnings.join(" · ")}
        </div>
      ) : null}
      <div className="mt-3">
        <h2 className="h6">
          {i18n.t("teamFinances.mobileCommitmentTimeline", {
            ns: "ui",
            defaultValue: "Commitment timeline",
          })}
        </h2>
        {highestPressureYear ? (
          <p className="small mb-2">
            {i18n.t("teamFinances.mobileHighestPressureYear", {
              ns: "ui",
              defaultValue: "Highest pressure year: {{season}}",
              season: highestPressureYear.season,
            })}
          </p>
        ) : null}
        <div
          className="d-grid gap-2 mb-2"
          data-team-finances-year-list={allYearsOpen ? "expanded" : "preview"}
        >
          {visibleTimelineItems.map((item) => (
            <MobileStatusMetric
              detail={
                <>
                  {stripTrailingColon(
                    i18n.t("leagueDashboard.payroll", {
                      ns: "ui",
                      defaultValue: "Payroll:",
                    }),
                  )}
                  : {helpers.formatCurrency(item.committed, "M")} ·{" "}
                  {i18n.t("teamFinances.mobileCapSpaceLabel", {
                    ns: "ui",
                    defaultValue: "Cap space",
                  })}
                  : {helpers.formatCurrency(item.distance, "M")}
                </>
              }
              key={item.season}
              label={item.season}
              score={item.capStatus.score}
              value={capStatusLabel(item.capStatus.state, item.distance)}
            />
          ))}
        </div>
        {timelineItems.length > timelinePreviewLimit ? (
          <button
            aria-expanded={allYearsOpen}
            className="btn btn-link text-body-secondary d-block mx-auto"
            data-team-finances-all-years=""
            onClick={() => setAllYearsOpen((open) => !open)}
            type="button"
          >
            {allYearsOpen
              ? i18n.t("teamFinances.mobileCollapseYears", {
                  ns: "ui",
                  defaultValue: "Show fewer years",
                })
              : i18n.t("teamFinances.mobileExpandAllYears", {
                  ns: "ui",
                  defaultValue: "View all years",
                })}
          </button>
        ) : null}
      </div>
    </>
  );
};

const RevenueBarGraph = ({ barData }: { barData: View<"teamFinances">["barData"] }) => (
  <BarGraph
    data={barData}
    y={[
      "revenuesNationalTv",
      "revenuesLocalTv",
      "revenuesTicket",
      "revenuesSponsor",
      "revenuesMerch",
      "revenuesLuxuryTaxShare",
    ]}
    tooltip={(row, y) => {
      const text = {
        revenuesNationalTv: i18n.t("teamFinances.revNationalTv", {
          ns: "ui",
          defaultValue: "national TV revenue",
        }),
        revenuesLocalTv: i18n.t("teamFinances.revLocalTv", {
          ns: "ui",
          defaultValue: "local TV revenue",
        }),
        revenuesTicket: i18n.t("teamFinances.revTicket", {
          ns: "ui",
          defaultValue: "ticket revenue",
        }),
        revenuesSponsor: i18n.t("teamFinances.revSponsor", {
          ns: "ui",
          defaultValue: "corporate sponsorship revenue",
        }),
        revenuesMerch: i18n.t("teamFinances.revMerch", {
          ns: "ui",
          defaultValue: "merchandising revenue",
        }),
        revenuesLuxuryTaxShare: i18n.t("teamFinances.revLuxuryTaxShare", {
          ns: "ui",
          defaultValue: "luxury tax share revenue",
        }),
      };

      return `${row.season} ${text[y]}: ${helpers.formatCurrency(row[y] / 1000, "M", 1)}`;
    }}
  />
);

const ExpensesBarGraph = ({ barData }: { barData: View<"teamFinances">["barData"] }) => (
  <BarGraph
    data={barData}
    y={[
      "expensesSalary",
      "expensesScouting",
      "expensesCoaching",
      "expensesHealth",
      "expensesFacilities",
      "expensesMinTax",
      "expensesLuxuryTax",
    ]}
    tooltip={(row, y) => {
      const text = {
        expensesSalary: i18n.t("teamFinances.expSalaries", {
          ns: "ui",
          defaultValue: "player salaries",
        }),
        expensesScouting: i18n.t("teamFinances.expScouting", {
          ns: "ui",
          defaultValue: "scouting",
        }),
        expensesCoaching: i18n.t("teamFinances.expCoaching", {
          ns: "ui",
          defaultValue: "coaching",
        }),
        expensesHealth: i18n.t("teamFinances.expHealth", {
          ns: "ui",
          defaultValue: "health",
        }),
        expensesFacilities: i18n.t("teamFinances.expFacilities", {
          ns: "ui",
          defaultValue: "facilities",
        }),
        expensesMinTax: i18n.t("teamFinances.expMinTax", {
          ns: "ui",
          defaultValue: "minimum payroll tax",
        }),
        expensesLuxuryTax: i18n.t("teamFinances.expLuxuryTax", {
          ns: "ui",
          defaultValue: "luxury tax",
        }),
      };

      return `${row.season} ${text[y]}: ${helpers.formatCurrency(row[y] / 1000, "M", 1)}`;
    }}
  />
);

const CashBarGraph = ({
  barData,
  champSuffix,
  classNameOverride,
}: {
  barData: View<"teamFinances">["barData"];
  champSuffix: (row: TeamFinancesBarDataRow) => string;
  classNameOverride: (row: TeamFinancesBarDataRow) => string | undefined;
}) => (
  <BarGraph
    data={barData}
    y={["cash"]}
    tooltip={(row) =>
      `${row.season}: ${helpers.formatCurrency(row.cash, "M", 1)}${champSuffix(row)}`
    }
    classNameOverride={classNameOverride}
  />
);

// v5Content: the old revenue/expenses/cash tab switcher is converged into a
// single stacked history section (three labelled charts, no tab state). It
// stays inside the "Financial history" expert fold as depth content.
export const TeamFinancesFinancialHistory = ({
  barData,
  champSuffix,
  classNameOverride,
}: {
  barData: View<"teamFinances">["barData"];
  champSuffix: (row: TeamFinancesBarDataRow) => string;
  classNameOverride: (row: TeamFinancesBarDataRow) => string | undefined;
}) => (
  <section
    className="team-finances-history-stacked"
    aria-label={i18n.t("teamFinances.financialHistory", {
      ns: "ui",
      defaultValue: "Financial history",
    })}
  >
    <h3 className="h6">{i18n.t("teamFinances.revenue", { ns: "ui", defaultValue: "Revenue" })}</h3>
    <div id="bar-graph-revenue-mobile" className="bar-graph-large">
      <RevenueBarGraph barData={barData} />
    </div>
    <h3 className="h6 mt-3">
      {i18n.t("teamFinances.expenses", { ns: "ui", defaultValue: "Expenses" })}
    </h3>
    <div id="bar-graph-expenses-mobile" className="bar-graph-large">
      <ExpensesBarGraph barData={barData} />
    </div>
    <h3 className="h6 mt-3">{i18n.t("teamFinances.cash", { ns: "ui", defaultValue: "Cash" })}</h3>
    <div id="bar-graph-cash-mobile" className="bar-graph-medium">
      <CashBarGraph
        barData={barData}
        champSuffix={champSuffix}
        classNameOverride={classNameOverride}
      />
    </div>
  </section>
);

const highlightZeroNegative = (amount: number) => {
  const formattedValue = helpers.formatCurrency(amount, "M");

  if (amount === 0) {
    return { classNames: "text-body-secondary", value: formattedValue };
  }
  if (amount < 0) {
    return { classNames: "text-danger", value: formattedValue };
  }

  return formattedValue;
};

const TeamFinances = ({
  abbrev,
  autoTicketPrice,
  barData,
  budget,
  challengeNoRatings,
  contractTotals,
  contracts,
  luxuryPayroll,
  luxuryTax,
  luxuryTaxAmount,
  maxStadiumCapacity,
  minPayroll,
  minPayrollAmount,
  numGames,
  otherTeamTicketPrices,
  spectator,
  payroll,
  phase,
  salariesSeasons,
  salaryCap,
  salaryCapType,
  show,
  t,
  tid,
  userTid,
}: View<"teamFinances">) => {
  useTitleBar({
    title: i18n.t("teamFinances.title", { ns: "ui", defaultValue: "Team Finances" }),
    dropdownView: "team_finances",
    dropdownFields: { teams: abbrev, shows: show },
  });

  const { gameSimInProgress } = useLocalPartial(["gameSimInProgress"]);
  const [playerSalariesOpen, setPlayerSalariesOpen] = useState(false);
  const playerSalariesRef = useRef<HTMLDivElement>(null);

  const openPlayerSalaries = () => {
    setPlayerSalariesOpen(true);

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        playerSalariesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  const cols = getCols(["Pos", "Name", "Cap%"]).concat(
    salariesSeasons.map((season) => {
      return {
        title: String(season),
        sortSequence: ["desc", "asc"],
        sortType: "number",
      };
    }),
  );

  const rows = contracts.map((p, i) => {
    const data: DataTableRow["data"] = [
      wrappedPosition(p.pos),
      wrappedPlayerNameLabels({
        injury: p.injury,
        jerseyNumber: p.jerseyNumber,
        pid: p.pid,
        skills: p.skills,
        style: { fontStyle: p.released ? "italic" : "normal" },
        defaultWatch: p.watch,
        firstName: p.firstName,
        firstNameShort: p.firstNameShort,
        lastName: p.lastName,
        nameOnly: true,
      }),
      p.capPct.toFixed(1),
    ];

    // Loop through the salaries for the next five years for this player.
    for (let j = 0; j < salariesSeasons.length; j++) {
      if (p.amounts[j]) {
        const amount = p.amounts[j]!;
        const formattedAmount = helpers.formatCurrency(amount, "M");

        if (p.released) {
          data.push({
            value: <i>{formattedAmount}</i>,
            sortValue: amount,
            searchValue: formattedAmount,
          });
        } else {
          data.push({
            value: formattedAmount,
            sortValue: amount,
            searchValue: formattedAmount,
          });
        }
      } else {
        data.push(null);
      }
    }

    return {
      key: i, // Can't be pid because a player will appear twice if he is cut and re-signed
      data,
    };
  });

  const footer: FooterRow[] = [
    {
      data: [
        "",
        i18n.t("teamFinances.totals", { ns: "ui", defaultValue: "Totals" }),
        "",
        ...contractTotals.map((amount) => highlightZeroNegative(amount)),
      ],
    },
    {
      data:
        salaryCapType === "none"
          ? [
              "",
              i18n.t("teamFinances.underLuxuryTax", {
                ns: "ui",
                defaultValue: "Under Luxury Tax",
              }),
              "",
              ...contractTotals.map((amount) => highlightZeroNegative(luxuryPayroll - amount)),
            ]
          : [
              "",
              i18n.t("teamFinances.freeCapSpace", {
                ns: "ui",
                defaultValue: "Free Cap Space",
              }),
              "",
              ...contractTotals.map((amount) => highlightZeroNegative(salaryCap - amount)),
            ],
    },
  ];

  // This happens for expansion teams before they have a TeamSeason
  const noSeasonData = barData.length === 0;

  type Row = (typeof barData)[number];
  const classNameOverride = (row: Row) => (row.champ ? "bar-graph-4" : undefined);
  const champSuffix = (row: Row) =>
    row.champ
      ? i18n.t("teamFinances.wonChampionship", { ns: "ui", defaultValue: ", won championship" })
      : "";
  const renderFinancesForm = () =>
    budget && tid === userTid && !spectator ? (
      <FinancesForm
        key={tid}
        autoTicketPrice={autoTicketPrice}
        challengeNoRatings={challengeNoRatings}
        gameSimInProgress={gameSimInProgress}
        otherTeamTicketPrices={otherTeamTicketPrices}
        phase={phase}
        salaryCap={salaryCap}
        spectator={spectator}
        t={t}
        tid={tid}
        userTid={userTid}
      />
    ) : null;
  const canEditBudget = Boolean(budget && tid === userTid && !spectator);
  const isOtherTeam = tid !== userTid;
  const financeRisk = getTeamFinanceRisk({
    luxuryTaxAmount,
    minPayrollAmount,
    payroll,
    salaryCap,
    salaryCapType,
  });
  const primaryActionPlan = getTeamFinancePrimaryActionPlan({
    canEditBudget,
    isOtherTeam,
    nextAction: financeRisk.nextAction,
  });
  const historyRange = show === "all" ? "all" : "10";
  const historyRangeLabel =
    historyRange === "all"
      ? i18n.t("teamFinances.historyRangeAll", {
          ns: "ui",
          defaultValue: "All available seasons",
        })
      : i18n.t("teamFinances.historyRangeLastTen", {
          ns: "ui",
          defaultValue: "Last 10 seasons",
        });
  const teamRouteParam = tid >= 0 ? `${abbrev}_${tid}` : undefined;
  const fallbackRosterHref = teamRouteParam
    ? helpers.leagueUrl(["roster", teamRouteParam])
    : helpers.leagueUrl(["roster"]);
  const rosterContext = resolveTeamFinancesRosterContext({
    fallbackHref: fallbackRosterHref,
    search: typeof window !== "undefined" ? window.location.search : undefined,
  });
  const marketActionLabel =
    primaryActionPlan === "addPayroll"
      ? i18n.t("teamFinances.primaryAddPayroll", {
          ns: "ui",
          defaultValue: "Add payroll through Market",
        })
      : primaryActionPlan === "manageCapOptions"
        ? i18n.t("teamFinances.primaryManageCapOptions", {
            ns: "ui",
            defaultValue: "Review cap options in Market",
          })
        : primaryActionPlan === "reviewMarket"
          ? i18n.t("teamFinances.primaryReviewMarket", {
              ns: "ui",
              defaultValue: "Review market options",
            })
          : undefined;
  const primaryAction =
    primaryActionPlan === "reviewContracts" ? (
      <button
        className={mobileActionButtonClassNames.primary}
        data-team-finances-primary-review-contracts=""
        onClick={openPlayerSalaries}
        type="button"
      >
        {i18n.t("teamFinances.contextRosterContracts", {
          ns: "ui",
          defaultValue: "Review roster contracts",
        })}
      </button>
    ) : marketActionLabel ? (
      <a className={mobileActionButtonClassNames.primary} href={helpers.leagueUrl(["market"])}>
        {marketActionLabel}
      </a>
    ) : primaryActionPlan === "tradeFit" ? (
      // Trade fit is a market sub-page (not a root), so seed a return so the back
      // control climbs back to this team's finances (look-up cross-tab CTA).
      <a
        className={mobileActionButtonClassNames.secondary}
        href={createMobileCrossTabHref(helpers.leagueUrl(["trade"]))}
      >
        <span className="visually-hidden">
          {i18n.t("teamFinances.p2s05TradeFit", { ns: "ui", defaultValue: "Trade fit" })}
        </span>
        {i18n.t("teamFinances.reviewTradeFit", { ns: "ui", defaultValue: "Review trade fit" })}
      </a>
    ) : undefined;
  const showScoutedRosterReturn = rosterContext.kind === "scoutedRosterReturn";
  const showContractShortcut =
    rosterContext.kind === "currentTeamFallback" && primaryActionPlan !== "reviewContracts";
  return (
    <div data-team-finances-history-range={historyRange} data-v5-teamfinances-profile="">
      {/* v5Content: the cap-health numbers ARE the mobile first screen. The old
          management-page eyebrow/subtitle narration and the decision-summary
          "cap health / trade watch" story card are gone (法则一); for other-team
          scouting the single scouting read is the one allowed explainer line. */}
      <MobileContentHero
        action={primaryAction}
        className="team-finances-cap-health d-md-none"
        data-v5-teamfinances-content-first-screen=""
        data-v5-teamfinances-cap-health-first-screen=""
      >
        <TeamFinancesMobileSummary
          contractTotals={contractTotals}
          financeRisk={financeRisk}
          luxuryPayroll={luxuryPayroll}
          luxuryTax={luxuryTax}
          luxuryTaxAmount={luxuryTaxAmount}
          minPayroll={minPayroll}
          minPayrollAmount={minPayrollAmount}
          payroll={payroll}
          salariesSeasons={salariesSeasons}
          salaryCap={salaryCap}
          salaryCapType={salaryCapType}
        />
      </MobileContentHero>

      {teamRouteParam && (showScoutedRosterReturn || showContractShortcut) ? (
        <nav
          aria-label={i18n.t("teamFinances.contextLinksLabel", {
            ns: "ui",
            defaultValue: "Team finance actions",
          })}
          className="more-links-single"
          data-team-finances-context-links=""
        >
          {showScoutedRosterReturn ? (
            <a data-team-finances-roster-context={rosterContext.kind} href={rosterContext.href}>
              {i18n.t("roster.returnToScoutedRoster", {
                ns: "ui",
                defaultValue: "Return to scouted roster",
              })}
            </a>
          ) : (
            <button
              className="btn btn-link btn-sm p-0"
              data-team-finances-contract-shortcut=""
              data-team-finances-roster-context={rosterContext.kind}
              onClick={openPlayerSalaries}
              type="button"
            >
              {i18n.t("teamFinances.contextRosterContracts", {
                ns: "ui",
                defaultValue: "Review roster contracts",
              })}
            </button>
          )}
        </nav>
      ) : null}

      <div className="team-finances-payroll-context">
        <PayrollInfo
          luxuryPayroll={luxuryPayroll}
          luxuryTax={luxuryTax}
          luxuryTaxAmount={luxuryTaxAmount}
          minPayroll={minPayroll}
          minPayrollAmount={minPayrollAmount}
          payroll={payroll}
          salaryCap={salaryCap}
          salaryCapType={salaryCapType}
        />
      </div>

      {budget ? null : (
        <p className="text-danger">
          {i18n.t("teamFinances.budgetDisabledSimple", {
            ns: "ui",
            defaultValue:
              "The budget is disabled in this league, so most of the information usually shown here is hidden.",
          })}
        </p>
      )}

      {budget && !noSeasonData ? (
        <div
          className="team-finances-mobile-history-context d-md-none mb-3"
          data-team-finances-history-context=""
        >
          <div className="small fw-semibold">{historyRangeLabel}</div>
          <details
            className="team-finances-mobile-history"
            data-mobile-team-finance-layer="history"
            data-native-details-host="team-finances-history"
          >
            <summary>
              {i18n.t("teamFinances.financialHistory", {
                ns: "ui",
                defaultValue: "Financial history",
              })}
            </summary>
            <TeamFinancesFinancialHistory
              barData={barData}
              champSuffix={champSuffix}
              classNameOverride={classNameOverride}
            />
          </details>
        </div>
      ) : null}

      <div className="row team-finances-budget-row">
        <div className="col-md-3 col-sm-2 d-none d-md-block">
          <h3>{i18n.t("teamFinances.wins", { ns: "ui", defaultValue: "Wins" })}</h3>
          <div className="bar-graph-small">
            <BarGraph
              data={barData}
              y={["won"]}
              tooltip={(row) => `${row.season}: ${row.won}${champSuffix(row)}`}
              ylim={[0, numGames]}
              classNameOverride={classNameOverride}
            />
          </div>
          <br />
          <br />
          <h3>{i18n.t("teamFinances.hype", { ns: "ui", defaultValue: "Hype" })}</h3>
          <div id="bar-graph-hype" className="bar-graph-small">
            <BarGraph
              data={barData}
              y={["hype"]}
              tooltip={(row) => {
                const hypeStatus = getTeamHypeStatus(row.hype);
                return `${row.season}: ${Math.round(row.hype * 100)}% · ${teamHypeLabel(hypeStatus.state)}${champSuffix(row)}`;
              }}
              ylim={[0, 1]}
              classNameOverride={classNameOverride}
            />
          </div>
          <br />
          <br />
          <h3>
            {i18n.t("teamFinances.regionPopulation", {
              ns: "ui",
              defaultValue: "Region Population",
            })}
          </h3>
          <div id="bar-graph-pop" className="bar-graph-small">
            <BarGraph
              data={barData}
              y={["pop"]}
              tooltip={(row) => `${row.season}: ${row.pop.toFixed(1)}M${champSuffix(row)}`}
              ylim={[0, 20]}
              classNameOverride={classNameOverride}
            />
          </div>
          <br />
          <br />
          <h3>
            {i18n.t("teamFinances.avgAttendance", { ns: "ui", defaultValue: "Average Attendance" })}
          </h3>
          <div id="bar-graph-att" className="bar-graph-small">
            <BarGraph
              data={barData}
              y={["att"]}
              tooltip={(row) =>
                `${row.season}: ${helpers.numberWithCommas(Math.round(row.att))}${champSuffix(row)}`
              }
              ylim={[0, maxStadiumCapacity]}
              classNameOverride={classNameOverride}
            />
          </div>
        </div>
        {budget && !noSeasonData ? (
          <div className="col-lg-4 col-md-3 col-sm-3 mb-3 d-none d-md-block">
            <h3>{i18n.t("teamFinances.revenue", { ns: "ui", defaultValue: "Revenue" })}</h3>
            <div id="bar-graph-revenue" className="bar-graph-large">
              <RevenueBarGraph barData={barData} />
            </div>
            <br />
            <br />
            <h3>{i18n.t("teamFinances.expenses", { ns: "ui", defaultValue: "Expenses" })}</h3>
            <div id="bar-graph-expenses" className="bar-graph-large">
              <ExpensesBarGraph barData={barData} />
            </div>
            <br />
            <br />
            <h3>
              {i18n.t("teamFinances.cashCumulative", {
                ns: "ui",
                defaultValue: "Cash (cumulative)",
              })}
            </h3>
            <div id="bar-graph-cash" className="bar-graph-medium">
              <CashBarGraph
                barData={barData}
                champSuffix={champSuffix}
                classNameOverride={classNameOverride}
              />
            </div>
          </div>
        ) : null}
        {budget ? (
          <div className="col-12 col-lg-5 col-md-6 team-finances-budget-detail-col">
            <details
              className="team-finances-advanced-settings"
              data-native-details-host="team-finances-budget-detail"
            >
              <summary>
                {i18n.t("teamFinances.budgetDetail", {
                  ns: "ui",
                  defaultValue: "Budget detail",
                })}
              </summary>
              {canEditBudget ? (
                renderFinancesForm()
              ) : (
                <p className="text-body-secondary mt-2">
                  {i18n.t("teamFinances.readonlyBudgetDetail", {
                    ns: "ui",
                    defaultValue: "Budget controls are hidden because this team is read-only here.",
                  })}
                </p>
              )}
            </details>
          </div>
        ) : null}
      </div>

      {/* B3: full-table entry follows the P2S expert-depth pattern — a counted
          MobileExpertDisclosure hosts the DataTable (pruned card list on
          mobile, unchanged table on desktop). */}
      <div ref={playerSalariesRef}>
        <MobileExpertDisclosure
          className="team-finances-player-salaries"
          count={i18n.t("teamFinances.playerSalariesCount", {
            ns: "ui",
            defaultValue: "{{count}} contracts",
            count: contracts.length,
          })}
          data-team-finances-player-salaries=""
          expertHostId="team-finances-player-salaries"
          onToggle={(event) => {
            setPlayerSalariesOpen(event.currentTarget.open);
          }}
          open={playerSalariesOpen}
          summary={i18n.t("teamFinances.playerSalaries", {
            ns: "ui",
            defaultValue: "Player Salaries",
          })}
        >
          <DataTable
            cols={cols}
            defaultSort={[3, "desc"]}
            name="TeamFinances"
            nonfluid
            footer={footer}
            rows={rows}
          />
        </MobileExpertDisclosure>
      </div>
    </div>
  );
};

export default TeamFinances;
